import { Directive, Input, OnInit } from '@angular/core';
import BlockModel from '../../../../../models/email-template/block-model.interface';
import {
  PreviewDevice,
  PreviewPlatform,
  PreviewVariant,
} from './preview-variant.interface';

/**
 * Abstract shell shared by every Google-template preview (Demand Gen single
 * image, and future types). It owns the whole selector experience — platform
 * tabs, device toggle, placement stepper — and renders the active variant via
 * ngComponentOutlet, feeding it a flat render DTO.
 *
 * A concrete preview only has to supply two things:
 *   - {@link registry}: its list of {@link PreviewVariant}s (single source of truth).
 *   - {@link buildPreviewData}: how to turn the raw blocks into its flat DTO.
 *
 * Everything else — filtering by platform/device, prev/next wrap-around, which
 * chrome is shown — is derived from the registry, so a new preview type is just
 * a subclass plus a registry array, and a new placement is one registry line.
 *
 * `TData` is the concrete preview's flat DTO type (e.g. GenSingleImagePreviewData);
 * it flows through to the variant components' `@Input() data`.
 *
 * Follows the repo's abstract-component convention (`@Directive()` + `@Input()`),
 * mirroring BaseTemplateEditorComponent.
 */
@Directive()
export abstract class BasePreviewShellComponent<TData> implements OnInit {
  @Input() blocks: BlockModel[] = [];
  @Input() language = 'en';

  platform: PreviewPlatform = 'discover';
  device: PreviewDevice = 'desktop';
  placementIndex = 0;

  /**
   * Display name per platform. Typed as a `Record` so adding a
   * {@link PreviewPlatform} member fails to compile until it's labelled here —
   * the template never has to know the platform set.
   */
  private readonly platformLabels: Record<PreviewPlatform, string> = {
    google: 'Google',
    youtube: 'YouTube',
    gmail: 'Gmail',
    display: 'Display',
    discover: 'Discover',
    facebook: 'Facebook',
    instagram: 'Instagram',
    threads: 'Threads',
    'audience-network': 'Audience Network',
  };

  /** Display name + Material icon per device, exhaustive over {@link PreviewDevice}. */
  private readonly deviceMeta: Record<
    PreviewDevice,
    { label: string; icon: string }
  > = {
      desktop: { label: 'Desktop', icon: 'computer' },
      mobile: { label: 'Mobile', icon: 'smartphone' },
      tv: { label: 'TV', icon: 'tv' },
    };

  /** The single source of truth for this preview type's variants. */
  protected abstract readonly registry: PreviewVariant[];

  /** Build this preview type's flat render DTO from {@link blocks}. */
  protected abstract buildPreviewData(): TData;

  ngOnInit(): void {
    // Snap the initial selection onto something the registry actually offers,
    // so single-platform or desktop-only previews start on a valid surface.
    if (!this.platforms.includes(this.platform)) {
      this.platform = this.platforms[0] ?? this.platform;
    }
    if (!this.devices.includes(this.device)) {
      this.device = this.devices[0] ?? this.device;
    }
  }

  /** Distinct platforms in the registry — drives whether the tabs are shown. */
  get platforms(): PreviewPlatform[] {
    return [...new Set(this.registry.map((v) => v.platform))];
  }

  /** Distinct devices for the current platform — drives whether the toggle shows. */
  get devices(): PreviewDevice[] {
    return [
      ...new Set(
        this.registry
          .filter((v) => v.platform === this.platform)
          .map((v) => v.device),
      ),
    ];
  }

  /** Variants matching the current platform + device, in registry order. */
  get variants(): PreviewVariant[] {
    return this.registry.filter(
      (v) => v.platform === this.platform && v.device === this.device,
    );
  }

  get active(): PreviewVariant | undefined {
    return this.variants[this.placementIndex];
  }

  /** Inputs object passed to the active variant via ngComponentOutlet. */
  get variantInputs(): { data: TData } {
    return { data: this.buildPreviewData() };
  }

  /** Display name for a platform tab. */
  platformLabel(platform: PreviewPlatform): string {
    return this.platformLabels[platform];
  }

  /** Display name for a device toggle. */
  deviceLabel(device: PreviewDevice): string {
    return this.deviceMeta[device].label;
  }

  /** Material icon name for a device toggle. */
  deviceIcon(device: PreviewDevice): string {
    return this.deviceMeta[device].icon;
  }

  selectPlatform(platform: PreviewPlatform): void {
    this.platform = platform;
    // Snap onto a device the new platform actually offers — otherwise a
    // desktop-only tab reached from a mobile one filters down to zero variants
    // and the stage renders blank. `devices` already reads the new platform.
    if (!this.devices.includes(this.device)) {
      this.device = this.devices[0] ?? this.device;
    }
    this.placementIndex = 0;
  }

  selectDevice(device: PreviewDevice): void {
    this.device = device;
    this.placementIndex = 0;
  }

  prev(): void {
    const count = this.variants.length;
    if (count > 0) {
      this.placementIndex = (this.placementIndex - 1 + count) % count;
    }
  }

  next(): void {
    const count = this.variants.length;
    if (count > 0) {
      this.placementIndex = (this.placementIndex + 1) % count;
    }
  }
}
